import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';
const RECONNECT_DELAY = 3000;

// ─── Singleton SSE connection ────────────────────────────────────
// All hook instances share one EventSource. Listeners register via
// addSSEListener / removeSSEListener; the connection opens on the
// first registration and closes when all are gone.

type SSEEventType = 'new_match' | 'spark_revealed' | 'date_plan_ready' | 'chat_message' | 'couple_formed' | 'partner_pick' | 'ping';
type SSEListener = (type: SSEEventType, data: Record<string, unknown>) => void;

let es: EventSource | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let getTokenFn: (() => Promise<string | null>) | null = null;
const listeners = new Set<SSEListener>();

function onEvent(type: SSEEventType, rawData: string) {
  let data: Record<string, unknown> = {};
  try { data = JSON.parse(rawData); } catch { /* ignore */ }
  for (const fn of listeners) fn(type, data);
}

function connect() {
  if (es || !getTokenFn || listeners.size === 0) return;
  getTokenFn().then(token => {
    if (!token || listeners.size === 0) return;
    const url = `${API_BASE}/api/events?token=${encodeURIComponent(token)}`;
    es = new EventSource(url);

    const EVENTS: SSEEventType[] = ['ping', 'new_match', 'spark_revealed', 'date_plan_ready', 'chat_message', 'couple_formed', 'partner_pick'];
    for (const type of EVENTS) {
      es.addEventListener(type, (e: Event) => onEvent(type, (e as MessageEvent).data));
    }

    es.onerror = () => {
      es?.close();
      es = null;
      if (listeners.size > 0) {
        reconnectTimer = setTimeout(connect, RECONNECT_DELAY);
      }
    };
  });
}

function disconnect() {
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  es?.close();
  es = null;
}

function addSSEListener(fn: SSEListener, getToken: () => Promise<string | null>) {
  getTokenFn = getToken;
  listeners.add(fn);
  if (!es) connect();
}

function removeSSEListener(fn: SSEListener) {
  listeners.delete(fn);
  if (listeners.size === 0) disconnect();
}

// ─── Hook ────────────────────────────────────────────────────────

/**
 * Subscribe to server-sent events. Pass an `onChatMessage` callback to
 * react to incoming chat messages for a specific room.
 * The hook also auto-invalidates TanStack Query keys on match/reveal/plan events.
 */
export function useSSE(
  onChatMessage?: (data: { chatRoomId: string }) => void,
  onDatePlanReady?: (data: { matchId: string }) => void,
) {
  const { getIdToken, isAuthenticated, refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const callbackRef = useRef(onChatMessage);
  callbackRef.current = onChatMessage;
  const planCallbackRef = useRef(onDatePlanReady);
  planCallbackRef.current = onDatePlanReady;
  const refreshProfileRef = useRef(refreshProfile);
  refreshProfileRef.current = refreshProfile;

  useEffect(() => {
    if (!isAuthenticated) return;

    const listener: SSEListener = (type, data) => {
      switch (type) {
        case 'new_match':
          // Invalidate feed so the passed-over user disappears; refetch matches in background
          queryClient.invalidateQueries({ queryKey: ['feed'] });
          queryClient.invalidateQueries({ queryKey: ['matches'] });
          break;
        case 'spark_revealed':
          queryClient.invalidateQueries({ queryKey: ['matches'] });
          break;
        case 'date_plan_ready': {
          const matchId = (data as any).matchId as string | undefined;
          queryClient.invalidateQueries({ queryKey: ['matches'] });
          if (matchId) planCallbackRef.current?.({ matchId });
          break;
        }
        case 'couple_formed':
          // The user's mode just flipped to COUPLES — refresh profile so the
          // feed/UI switch over, and refetch couple-aware queries.
          refreshProfileRef.current?.();
          queryClient.invalidateQueries({ queryKey: ['matches'] });
          queryClient.invalidateQueries({ queryKey: ['feed'] });
          queryClient.invalidateQueries({ queryKey: ['couple-me'] });
          queryClient.invalidateQueries({ queryKey: ['graduation-status'] });
          break;
        case 'partner_pick':
          // A partner staged/resolved a couple pick — refresh the picks strip and matches.
          queryClient.invalidateQueries({ queryKey: ['couple-picks'] });
          queryClient.invalidateQueries({ queryKey: ['matches'] });
          break;
        case 'chat_message':
          callbackRef.current?.(data as { chatRoomId: string });
          break;
      }
    };

    addSSEListener(listener, getIdToken);
    return () => removeSSEListener(listener);
  }, [isAuthenticated]);
}
